<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  STATUS_TAG,
  TYPE_LABELS,
  createVehicle,
  deleteVehicle,
  listVehicles,
  updateVehicle,
  type Vehicle,
  type VehicleInput,
  type VehicleRow,
} from '../api/vehicles'
import { listDriverOptions, type Driver } from '../api/drivers'

const rows = ref<VehicleRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const refError = ref<unknown>(null)
const notice = ref('')

const drivers = ref<Driver[]>([])

const query = reactive({
  keyword: '',
  status: '',
  type: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const editing = ref<Vehicle | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  plate_no: '',
  brand: '',
  type: 'van' as Vehicle['type'],
  capacity_kg: 0,
  capacity_cm3: 0,
  status: 'idle' as Vehicle['status'],
  driver_id: undefined as number | undefined,
})

const plateError = computed(() => (form.plate_no.trim() ? '' : '请输入车牌号'))
const formErrorText = computed(() => plateError.value)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listVehicles({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      type: query.type || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadDrivers(): Promise<void> {
  refError.value = null
  try {
    drivers.value = await listDriverOptions()
  } catch (e) {
    // 司机档案拿不到时绑定下拉会空，但必须把原因露出来，否则页面看着像没司机
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadDrivers()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    plate_no: '',
    brand: '',
    type: 'van' as Vehicle['type'],
    capacity_kg: 0,
    capacity_cm3: 0,
    status: 'idle' as Vehicle['status'],
    driver_id: undefined,
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Vehicle): void {
  editing.value = row
  Object.assign(form, {
    plate_no: row.plate_no,
    brand: row.brand,
    type: row.type,
    capacity_kg: row.capacity_kg,
    capacity_cm3: row.capacity_cm3,
    status: row.status,
    driver_id: row.driver_id || undefined,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: VehicleInput = {
    plate_no: form.plate_no.trim(),
    brand: form.brand.trim(),
    type: form.type,
    capacity_kg: Number(form.capacity_kg) || 0,
    capacity_cm3: Number(form.capacity_cm3) || 0,
    status: form.status,
    driver_id: form.driver_id,
  }
  try {
    if (editing.value) await updateVehicle(editing.value.id, input)
    else await createVehicle(input)
    formOpen.value = false
    await load(editing.value ? '车辆已更新' : '车辆已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Vehicle): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteVehicle(row.id)
    await load(`已删除车辆「${row.plate_no}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-veh">
    <header class="yc-veh__bar">
      <el-input
        v-model="query.keyword"
        class="yc-veh__keyword"
        placeholder="车牌 / 品牌"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.type" class="yc-veh__ref" placeholder="全部类型" clearable @change="search">
        <el-option v-for="(label, value) in TYPE_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select v-model="query.status" class="yc-veh__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'dsp:vehicle:create'" type="primary" @click="openCreate">新建车辆</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-veh__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-veh__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-veh__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="plate_no" label="车牌号" min-width="130" />
        <el-table-column prop="brand" label="品牌" min-width="120" show-overflow-tooltip />
        <el-table-column label="类型" width="110">
          <template #default="{ row }">
            {{ TYPE_LABELS[row.type as Vehicle['type']] ?? row.type }}
          </template>
        </el-table-column>
        <el-table-column label="载重（kg）" width="120" align="right">
          <template #default="{ row }">{{ Number(row.capacity_kg).toFixed(3) }}</template>
        </el-table-column>
        <el-table-column label="体积（cm³）" width="140" align="right">
          <template #default="{ row }">{{ Number(row.capacity_cm3).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as Vehicle['status']] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as Vehicle['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="绑定司机" min-width="130" show-overflow-tooltip>
          <template #default="{ row }">{{ row.driver_id ? row.driver_name || `#${row.driver_id}` : '未绑定' }}</template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'dsp:vehicle:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该车辆？已被行车任务引用的车辆不能删除。" width="280" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'dsp:vehicle:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有车辆"
        description="派车要把任务装进车里，先建一台有载重与容积的车。"
      >
        <el-button v-perm="'dsp:vehicle:create'" type="primary" @click="openCreate">新建车辆</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-veh__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑车辆 · ${editing.plate_no}` : '新建车辆'"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-veh__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-veh__formrow">
          <el-form-item label="车牌号" :error="plateError">
            <el-input v-model="form.plate_no" maxlength="32" placeholder="如 沪A·12345" />
          </el-form-item>

          <el-form-item label="品牌">
            <el-input v-model="form.brand" maxlength="64" placeholder="如 解放" />
          </el-form-item>
        </div>

        <div class="yc-veh__formrow">
          <el-form-item label="类型">
            <el-select v-model="form.type" class="yc-veh__full">
              <el-option v-for="(label, value) in TYPE_LABELS" :key="value" :label="label" :value="value" />
            </el-select>
          </el-form-item>

          <el-form-item label="状态">
            <el-select v-model="form.status" class="yc-veh__full">
              <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="yc-veh__formrow">
          <el-form-item label="载重（kg）">
            <el-input-number
              v-model="form.capacity_kg"
              :min="0"
              :max="999999"
              :precision="3"
              controls-position="right"
            />
          </el-form-item>

          <el-form-item label="体积（cm³）">
            <el-input-number
              v-model="form.capacity_cm3"
              :min="0"
              :max="9999999999"
              :precision="2"
              controls-position="right"
            />
          </el-form-item>
        </div>

        <el-form-item label="绑定司机">
          <el-select
            v-model="form.driver_id"
            class="yc-veh__full"
            clearable
            placeholder="可留空，绑定了派车时可自动带出"
          >
            <el-option v-for="d in drivers" :key="d.id" :label="`${d.code} ${d.name}`.trim()" :value="d.id" />
          </el-select>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-veh {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 180px;
  }

  &__ref {
    width: 140px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }
}
</style>
